import { DICE_COUNT, MAX_ROLLS, type ComboId } from '../game/types';
import { rngFromString, type Rng } from '../game/rng';
import { COMBO_BASE } from '../game/combos';
import { scoreHandCtx, TALISMAN_BY_ID, TALISMANS, type ScoredHandFull, type TalismanFamily } from '../game/talismans';
import { DIE_PRICE } from '../game/dice';
import {
  buyDie,
  buyTalisman,
  canRerollForced,
  canRerollFree,
  CHARACTERS,
  closeHand,
  currentBoss,
  isBossRound,
  leaveShop,
  newRun,
  rerollForced,
  rerollFree,
  rerollShop,
  scoreCtxOf,
  SHOP_REROLL_COST,
  startHand,
  talismanSlots,
  TILT_MAX,
  toggleHold,
  TOTAL_ROUNDS,
  type CharacterId,
  type HandOutcome,
  type RunState,
} from '../game/run';
import { getLang, setLang, t, type I18nKey } from '../lib/i18n';
import { tracking } from '../lib/tracker';
import { sfxFanfare, sfxHold, sfxRattle, sfxScoreTick, sfxSettle } from './audio';

type UiPhase = 'menu' | 'rolling' | 'choice' | 'scored' | 'shop' | 'over' | 'won';

const BEST_KEY = 'tilt:best-round';
const ROLL_FLICKER_MS = 80;
const ROLL_BASE_MS = 420;
const ROLL_STAGGER_MS = 90;
const WINDUP_MS = 90;
const STAMP_MS = 120;

const fmtMult = (m: number) => (Number.isInteger(m) ? String(m) : m.toFixed(1));

/**
 * Orchestrazione UI della run (Sprint 1). Tutta la casualità di gioco passa
 * dall'rng seedato dentro l'engine; Math.random qui è SOLO cosmesi.
 */
export class App {
  private phase: UiPhase = 'menu';
  private run: RunState | null = null;
  private rng: Rng = rngFromString('boot');
  private runCounter = 0;
  private bestRound = 0;
  private skipRequested = false;
  private detachSkip: (() => void) | null = null;

  private root: HTMLElement;
  private dice: HTMLButtonElement[] = [];
  private els!: Record<
    | 'tagline' | 'lang' | 'help' | 'bestBox' | 'hud' | 'strip' | 'rolls' | 'preview'
    | 'scorePanel' | 'actions' | 'note' | 'overlay' | 'guide',
    HTMLElement
  >;

  constructor(root: HTMLElement) {
    this.root = root;
    try {
      this.bestRound = Number(localStorage.getItem(BEST_KEY)) || 0;
    } catch { /* best effort */ }
    this.build();
    this.renderAll();
    tracking.gameStart();
    window.addEventListener('pagehide', () =>
      tracking.gameEnd({ duration: Math.round(performance.now() / 1000) }),
    );
  }

  // ── DOM ──────────────────────────────────────────────────────────────────

  private build(): void {
    this.root.innerHTML = `
      <header class="top">
        <div class="brand">
          <h1 class="logo">TILT</h1>
          <p class="tagline" data-el="tagline"></p>
        </div>
        <div class="top-right">
          <div class="best" data-el="best"></div>
          <button class="lang" data-el="help" type="button" aria-label="how to play">?</button>
          <button class="lang" data-el="lang" type="button"></button>
        </div>
      </header>
      <div class="hud" data-el="hud"></div>
      <div class="strip" data-el="strip"></div>
      <main class="table">
        <div class="dice" data-el="dice"></div>
        <div class="rolls" data-el="rolls"></div>
        <div class="preview" data-el="preview"></div>
        <div class="score-panel hidden" data-el="score"></div>
      </main>
      <footer class="bottom">
        <div class="actions" data-el="actions"></div>
        <p class="note" data-el="note"></p>
      </footer>
      <div class="overlay hidden" data-el="overlay"></div>
      <div class="overlay hidden" data-el="guide"></div>
    `;
    const q = (name: string): HTMLElement => this.root.querySelector(`[data-el="${name}"]`) as HTMLElement;
    this.els = {
      tagline: q('tagline'),
      lang: q('lang'),
      help: q('help'),
      bestBox: q('best'),
      hud: q('hud'),
      strip: q('strip'),
      rolls: q('rolls'),
      preview: q('preview'),
      scorePanel: q('score'),
      actions: q('actions'),
      note: q('note'),
      overlay: q('overlay'),
      guide: q('guide'),
    };
    this.els.help.addEventListener('click', () => this.openGuide());

    const diceBox = q('dice');
    for (let i = 0; i < DICE_COUNT; i++) {
      const die = document.createElement('button');
      die.type = 'button';
      die.className = 'die empty';
      die.dataset['face'] = '1';
      die.setAttribute('aria-label', `die ${i + 1}`);
      for (let p = 0; p < 9; p++) die.appendChild(document.createElement('span'));
      die.addEventListener('click', () => this.onDieTap(i));
      diceBox.appendChild(die);
      this.dice.push(die);
    }

    (this.els.lang as HTMLButtonElement).addEventListener('click', () => {
      setLang(getLang() === 'en' ? 'it' : 'en');
      this.renderAll();
    });
  }

  private renderAll(): void {
    this.renderTexts();
    this.renderHud();
    this.renderStrip();
    this.renderRolls();
    this.renderPreview();
    this.renderActions();
    if (this.phase === 'shop') this.renderShop();
    if (this.phase === 'over' || this.phase === 'won') this.renderEnd();
  }

  private renderTexts(): void {
    this.els.tagline.textContent = t('tagline');
    this.els.lang.textContent = getLang() === 'en' ? 'IT' : 'EN';
    this.els.bestBox.textContent = this.bestRound > 0 ? `★ R${this.bestRound}` : '';
    this.els.note.textContent = this.phase === 'menu' ? t('menu.hint') : '';
  }

  // ── HUD: round, obiettivo, mani, pips, livella TILT ─────────────────────

  private renderHud(): void {
    const run = this.run;
    if (!run || this.phase === 'menu') {
      this.els.hud.innerHTML = '';
      this.els.hud.classList.add('hidden');
      document.documentElement.style.setProperty('--tilt', '0');
      return;
    }
    this.els.hud.classList.remove('hidden');
    const pct = Math.min(100, Math.round((run.roundScore / run.target) * 100));
    const hands = Array.from({ length: 3 }, (_, i) =>
      `<span class="dot ${i < run.handsLeft ? 'full' : 'spent'}"></span>`).join('');
    const tiltPct = run.tilt / TILT_MAX;
    document.documentElement.style.setProperty('--tilt', run.tiltPrimed ? '1' : String(tiltPct * 0.6));
    const boss = currentBoss(run);
    const bossLine = boss
      ? `<div class="hud-boss">☠ ${t('boss.label')}: <b>${t(`boss.${boss}.name` as I18nKey)}</b> — ${t(`boss.${boss}.desc` as I18nKey)}</div>`
      : '';
    this.els.hud.innerHTML = `
      <div class="hud-row">
        <span class="hud-round">${t('hud.round', { n: run.round })}<span class="hud-total">/${TOTAL_ROUNDS}</span></span>
        <span class="hud-hands">${hands}</span>
        <span class="hud-pips">◉ ${run.pips}</span>
      </div>
      ${bossLine}
      <div class="hud-target">
        <div class="hud-bar"><div class="hud-fill${pct >= 100 ? ' done' : ''}" style="width:${pct}%"></div></div>
        <span class="hud-score">${run.roundScore}<span class="hud-goal">/${run.target}</span></span>
      </div>
      <div class="livella${run.tiltPrimed ? ' primed' : ''}${run.tiltPrimed && run.talismans.includes('risk-berserk') ? ' berserk' : ''}">
        <span class="livella-label">TILT</span>
        <div class="livella-track">
          <div class="livella-trail" style="width:${tiltPct * 100}%"></div>
          <div class="livella-bubble" style="left:${8 + tiltPct * 84}%"></div>
          <div class="livella-notch"></div>
        </div>
      </div>
    `;
  }

  /** Striscia talismani: pillole colorate per famiglia, tap = descrizione. */
  private renderStrip(): void {
    const run = this.run;
    if (!run || run.talismans.length === 0 || this.phase === 'menu') {
      this.els.strip.innerHTML = '';
      return;
    }
    this.els.strip.innerHTML = '';
    for (const id of run.talismans) {
      const def = TALISMAN_BY_ID.get(id)!;
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = `talpill fam-${def.family}`;
      pill.dataset['tal'] = id;
      pill.textContent = t(`tal.${id}.name` as I18nKey);
      pill.addEventListener('click', () => {
        this.els.note.textContent = `${t(`tal.${id}.name` as I18nKey)} — ${t(`tal.${id}.desc` as I18nKey)}`;
      });
      this.els.strip.appendChild(pill);
    }
  }

  // ── Interazione mano ─────────────────────────────────────────────────────

  private setFace(i: number, face: number): void {
    const die = this.dice[i];
    if (die) die.dataset['face'] = String(face);
  }

  private onDieTap(i: number): void {
    if (this.phase !== 'choice' || !this.run?.hand) return;
    toggleHold(this.run, i);
    const held = this.run.hand.held[i] === true;
    this.dice[i]?.classList.toggle('held', held);
    sfxHold(held);
    this.renderActions();
  }

  /** Scelta del personaggio: ognuno siede al tavolo con una regola diversa. */
  private openCharacterSelect(): void {
    const overlay = this.els.overlay;
    overlay.classList.remove('hidden');
    const cards = (Object.keys(CHARACTERS) as CharacterId[]).map((id) => `
      <button type="button" class="shopcard charcard" data-char="${id}">
        <span class="shopcard-name">${t(`char.${id}.name` as I18nKey)}</span>
        <span class="shopcard-desc">${t(`char.${id}.desc` as I18nKey)}</span>
      </button>`).join('');
    overlay.innerHTML = `
      <div class="sheet">
        <h2 class="sheet-title">${t('char.title')}</h2>
        <div class="shopgrid">${cards}</div>
      </div>
    `;
    overlay.querySelectorAll<HTMLButtonElement>('[data-char]').forEach((btn) => {
      btn.addEventListener('click', () => this.startRun(btn.dataset['char'] as CharacterId));
    });
  }

  private startRun(character: CharacterId): void {
    this.runCounter += 1;
    const seed = `free-${Date.now()}-${this.runCounter}`;
    this.rng = rngFromString(seed);
    this.run = newRun(seed, character);
    this.els.overlay.classList.add('hidden');
    tracking.runStart({ character });
    this.startHandUI();
  }

  private startHandUI(): void {
    const run = this.run;
    if (!run || run.phase !== 'hand') return;
    const wasPrimed = run.tiltPrimed;
    startHand(run, this.rng);
    this.els.scorePanel.classList.add('hidden');
    this.els.preview.classList.remove('hidden');
    this.dice.forEach((d) => d.classList.remove('held', 'empty', 'used', 'dimmed'));
    this.markDieKinds();
    this.animateRoll(run.hand!.faces.map((_, i) => i), wasPrimed);
  }

  /** Etichetta visiva dei dadi speciali (Sprint 1: bordo colorato + lettera). */
  private markDieKinds(): void {
    const run = this.run;
    if (!run) return;
    this.dice.forEach((d, i) => {
      const kind = run.kinds[i] ?? 'standard';
      d.dataset['kind'] = kind;
    });
  }

  private doReroll(forced: boolean): void {
    const run = this.run;
    if (!run?.hand) return;
    const wasPrimed = run.tiltPrimed;
    const before = run.hand.faces.slice();
    if (forced) rerollForced(run, this.rng);
    else rerollFree(run, this.rng);
    const moving = run.hand.faces.map((_, i) => i).filter((i) => run.hand!.held[i] !== true);
    moving.forEach((i) => this.setFace(i, before[i] ?? 1));
    this.animateRoll(moving, wasPrimed || (forced && run.tiltPrimed));
  }

  /** Anticipazione → tumble → settle a cascata (art-direction §7). */
  private animateRoll(indices: number[], chaos: boolean): void {
    const run = this.run;
    if (!run?.hand) return;
    this.phase = 'rolling';
    this.renderActions();
    this.renderHud();
    this.renderRolls();
    this.els.preview.textContent = chaos ? `⚡ ${t('tilt.chaos')}` : '';
    if (chaos) {
      document.body.classList.add('shake');
      window.setTimeout(() => document.body.classList.remove('shake'), 500);
    }
    const finalFaces = run.hand.faces.slice();
    const totalMs = WINDUP_MS + ROLL_BASE_MS + indices.length * ROLL_STAGGER_MS;

    indices.forEach((i) => this.dice[i]?.classList.add('windup'));
    window.setTimeout(() => {
      sfxRattle(totalMs - WINDUP_MS);
      indices.forEach((i) => {
        const die = this.dice[i];
        die?.classList.remove('windup');
        die?.classList.add('rolling');
      });
      const flicker = window.setInterval(() => {
        for (const i of indices) {
          if (this.dice[i]?.classList.contains('rolling')) {
            this.setFace(i, 1 + Math.floor(Math.random() * 6));
          }
        }
      }, ROLL_FLICKER_MS);

      indices.forEach((i, order) => {
        window.setTimeout(() => {
          const die = this.dice[i];
          die?.classList.remove('rolling');
          die?.classList.add('settle');
          this.setFace(i, finalFaces[i] ?? 1);
          sfxSettle();
          window.setTimeout(() => die?.classList.remove('settle'), 240);
        }, ROLL_BASE_MS + order * ROLL_STAGGER_MS);
      });

      window.setTimeout(() => {
        window.clearInterval(flicker);
        this.phase = 'choice';
        this.renderAll();
      }, ROLL_BASE_MS + indices.length * ROLL_STAGGER_MS + 120);
    }, WINDUP_MS);
  }

  private renderRolls(): void {
    const run = this.run;
    if (!run?.hand || this.phase === 'menu' || this.phase === 'scored') {
      this.els.rolls.textContent = '';
      return;
    }
    let left = Math.max(0, MAX_ROLLS - run.hand.rollsUsed);
    if (run.roundRerollPool !== null) left = Math.min(left, run.roundRerollPool);
    const dots = Array.from({ length: 2 }, (_, i) =>
      `<span class="dot ${i < left ? 'full' : 'spent'}"></span>`).join('');
    const forced = run.hand.forcedRerolls > 0 ? ` <span class="forced-count">+${run.hand.forcedRerolls}⚡</span>` : '';
    this.els.rolls.innerHTML = `${dots} <span>${t('hint.rollsLeft')}</span>${forced}`;
  }

  private currentScore(): ScoredHandFull | null {
    const run = this.run;
    if (!run?.hand) return null;
    return scoreHandCtx(run.hand.faces, scoreCtxOf(run));
  }

  private renderPreview(): void {
    if (this.phase !== 'choice') return;
    const s = this.currentScore();
    if (!s) return;
    this.els.preview.innerHTML =
      `<strong>${t(`combo.${s.best.id}` as I18nKey)}</strong>` +
      ` <span class="formula">${s.chips} × ${fmtMult(s.mult)} = ${s.score}</span>`;
  }

  // ── Chiusura mano e coreografia punteggio ────────────────────────────────

  private bank(): void {
    const run = this.run;
    if (!run || this.phase !== 'choice') return;
    const outcome = closeHand(run, this.rng);
    if (!outcome) return;
    this.phase = 'scored';
    this.renderActions();
    this.renderRolls();
    this.els.preview.classList.add('hidden');
    this.dice.forEach((d, i) => {
      const used = outcome.result.best.usedIndices.includes(i);
      d.classList.toggle('used', used);
      d.classList.toggle('dimmed', !used);
    });
    this.showScore(outcome);
  }

  private showScore(outcome: HandOutcome): void {
    const { result } = outcome;
    const panel = this.els.scorePanel;
    panel.classList.remove('hidden');
    const flash = result.triggered
      .map((id) => `<span class="talflash fam-${TALISMAN_BY_ID.get(id)!.family}">${t(`tal.${id}.name` as I18nKey)}</span>`)
      .join('');
    panel.innerHTML = `
      <div class="combo-name pop">${t(`combo.${result.best.id}` as I18nKey)}</div>
      ${flash ? `<div class="talflashes">${flash}</div>` : ''}
      <div class="breakdown">
        <span>${t('score.chips')} <b data-el="chips">0</b></span>
        <span class="x">×</span>
        <span>${t('score.mult')} <b class="mult-value" data-el="mult">${fmtMult(result.mult)}</b></span>
      </div>
      <div class="total" data-el="total">0</div>
    `;
    const level: 1 | 2 | 3 = result.score >= 300 || result.best.base >= 120 ? 3 : result.best.base >= 60 ? 2 : 1;
    sfxFanfare(level);
    if (result.best.id === 'six-kind') this.celebrateSestetto();
    if (outcome.shattered.length > 0) this.els.note.textContent = `💥 ${t('glass.broken')}`;
    this.beginSkippable();

    const chipsEl = panel.querySelector('[data-el="chips"]') as HTMLElement;
    const multEl = panel.querySelector('[data-el="mult"]') as HTMLElement;
    const totalEl = panel.querySelector('[data-el="total"]') as HTMLElement;
    if (level >= 2) totalEl.classList.add('glow');

    this.countUp(chipsEl, result.chips, 450, () => {
      multEl.classList.add('stamp');
      sfxSettle();
      this.delay(STAMP_MS, () => {
        if (level === 3 && !this.skipRequested) {
          document.body.classList.add('shake');
          window.setTimeout(() => document.body.classList.remove('shake'), 500);
        }
        this.countUp(totalEl, result.score, 650, () => {
          this.endSkippable();
          this.renderHud();
          this.afterScore(outcome);
        });
      });
    });
  }

  /** Il Sestetto deve urlare: zoom del tavolo, lampada che flasha, pioggia di pips. */
  private celebrateSestetto(): void {
    document.body.classList.add('sestetto');
    window.setTimeout(() => document.body.classList.remove('sestetto'), 900);
    const table = this.root.querySelector('.table');
    if (!table) return;
    for (let i = 0; i < 16; i++) {
      const p = document.createElement('span');
      p.className = 'pip-particle';
      p.style.setProperty('--dx', `${(Math.random() * 2 - 1) * 160}px`);
      p.style.setProperty('--dy', `${-40 - Math.random() * 200}px`);
      p.style.left = `${35 + Math.random() * 30}%`;
      p.style.top = '40%';
      p.style.animationDelay = `${Math.random() * 180}ms`;
      table.appendChild(p);
      window.setTimeout(() => p.remove(), 1400);
    }
  }

  private afterScore(outcome: HandOutcome): void {
    const run = this.run!;
    if (outcome.roundCleared) {
      tracking.roundCleared(run.round);
      window.setTimeout(() => {
        this.phase = 'shop';
        this.renderShop(outcome.reward);
        this.renderActions();
      }, 600);
      return;
    }
    if (outcome.runOver) {
      tracking.runEnd(run.bestHand, { round: run.round, won: false, character: run.character });
      if (run.round > this.bestRound) {
        this.bestRound = run.round;
        try {
          localStorage.setItem(BEST_KEY, String(this.bestRound));
        } catch { /* best effort */ }
      }
      window.setTimeout(() => {
        this.phase = 'over';
        this.renderEnd();
      }, 800);
      return;
    }
    this.renderActions();
  }

  private beginSkippable(): void {
    this.skipRequested = false;
    const onTap = (): void => {
      this.skipRequested = true;
    };
    document.addEventListener('pointerdown', onTap, { once: true });
    this.detachSkip = () => document.removeEventListener('pointerdown', onTap);
  }

  private endSkippable(): void {
    this.detachSkip?.();
    this.detachSkip = null;
    this.skipRequested = false;
  }

  private delay(ms: number, cb: () => void): void {
    if (this.skipRequested) {
      cb();
      return;
    }
    window.setTimeout(cb, ms);
  }

  private countUp(el: HTMLElement, target: number, durMs: number, onDone?: () => void): void {
    const start = performance.now();
    let lastTick = 0;
    const step = (now: number): void => {
      const p = this.skipRequested ? 1 : Math.min(1, (now - start) / durMs);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = String(Math.round(target * eased));
      if (now - lastTick > 55 && p < 1) {
        lastTick = now;
        sfxScoreTick(p);
      }
      if (p < 1) requestAnimationFrame(step);
      else onDone?.();
    };
    requestAnimationFrame(step);
  }

  // ── Il banco ─────────────────────────────────────────────────────────────

  private renderShop(reward?: number): void {
    const run = this.run;
    if (!run?.shop) return;
    const overlay = this.els.overlay;
    overlay.classList.remove('hidden');
    const slots = talismanSlots(run);
    const slotsFull = run.talismans.length >= slots;

    const talCards = run.shop.talismans
      .map((offer, i) => {
        const def = TALISMAN_BY_ID.get(offer.id)!;
        const afford = run.pips >= offer.price && !slotsFull;
        return `
          <button type="button" class="shopcard fam-${def.family} rar-${def.rarity}" data-buy="tal-${i}"
            ${offer.sold || !afford ? 'disabled' : ''}>
            <span class="shopcard-rarity">${t(`rarity.${def.rarity}` as I18nKey)}</span>
            <span class="shopcard-name">${t(`tal.${offer.id}.name` as I18nKey)}</span>
            <span class="shopcard-desc">${t(`tal.${offer.id}.desc` as I18nKey)}</span>
            <span class="shopcard-price">${offer.sold ? t('shop.sold') : `◉ ${offer.price}`}</span>
          </button>`;
      })
      .join('');

    const die = run.shop.die;
    const dieCard = die
      ? `
        <button type="button" class="shopcard diecard" data-buy="die"
          ${die.sold || run.pips < die.price ? 'disabled' : ''}>
          <span class="shopcard-rarity">${t('shop.die')}</span>
          <span class="shopcard-name">${t(`die.${die.kind}.name` as I18nKey)}</span>
          <span class="shopcard-desc">${t(`die.${die.kind}.desc` as I18nKey)}</span>
          <span class="shopcard-price">${die.sold ? t('shop.sold') : `◉ ${die.price}`}</span>
        </button>`
      : '';

    overlay.innerHTML = `
      <div class="sheet">
        <h2 class="sheet-title">${t('shop.title')}</h2>
        ${reward !== undefined ? `<p class="sheet-reward">+${reward} Pips · ${t('shop.cleared')}</p>` : ''}
        <p class="sheet-meta">◉ ${run.pips} · ${t('shop.slots', { a: run.talismans.length, b: slots })}</p>
        <div class="shopgrid">${talCards}${dieCard}</div>
        <div class="sheet-actions">
          <button type="button" class="btn secondary" data-buy="reroll" ${run.pips < SHOP_REROLL_COST ? 'disabled' : ''}>
            ${t('shop.reroll', { n: SHOP_REROLL_COST })}
          </button>
          <button type="button" class="btn primary" data-buy="leave">
            ${isBossRound(run.round + 1) ? '☠ ' : ''}${t('shop.continue', { n: run.round + 1 })}
          </button>
        </div>
      </div>
    `;
    overlay.querySelectorAll<HTMLButtonElement>('[data-buy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset['buy']!;
        if (action.startsWith('tal-')) {
          if (buyTalisman(run, Number(action.slice(4)))) sfxHold(true);
        } else if (action === 'die') {
          if (buyDie(run)) sfxHold(true);
        } else if (action === 'reroll') {
          if (rerollShop(run, this.rng)) sfxRattle(300);
        } else if (action === 'leave') {
          leaveShop(run);
          this.els.overlay.classList.add('hidden');
          if (run.phase === 'won') {
            tracking.runEnd(run.bestHand, { round: TOTAL_ROUNDS, won: true, character: run.character });
            if (run.round > this.bestRound || (run.phase === 'won' && TOTAL_ROUNDS >= this.bestRound)) {
              this.bestRound = TOTAL_ROUNDS;
              try {
                localStorage.setItem(BEST_KEY, String(this.bestRound));
              } catch { /* best effort */ }
            }
            this.phase = 'won';
            this.renderEnd();
            return;
          }
          this.phase = 'rolling';
          this.renderStrip();
          this.startHandUI();
          return;
        }
        this.renderShop(reward);
        this.renderStrip();
        this.renderHud();
      });
    });
  }

  // ── La guida completa: regole, combo, talismani, dadi ────────────────────

  private openGuide(): void {
    // Mini-dadi renderizzati come i dadi veri (CSS condiviso): leggibili, non glifi.
    const miniDie = (face: number): string =>
      `<span class="mini-die" data-face="${face}">${'<span></span>'.repeat(9)}</span>`;
    const ex = (faces: number[], ellipsis = false): string =>
      `${faces.map(miniDie).join('')}${ellipsis ? '<span class="combo-etc">…</span>' : ''}`;
    const COMBO_ORDER: { id: ComboId; example: string }[] = [
      { id: 'pair', example: ex([5, 5]) },
      { id: 'two-pair', example: ex([5, 5, 2, 2]) },
      { id: 'three-kind', example: ex([4, 4, 4]) },
      { id: 'small-straight', example: ex([2, 3, 4, 5]) },
      { id: 'full-house', example: ex([4, 4, 4, 2, 2]) },
      { id: 'large-straight', example: ex([2, 3, 4, 5, 6]) },
      { id: 'four-kind', example: ex([6, 6, 6, 6]) },
      { id: 'five-kind', example: ex([6, 6, 6, 6, 6]) },
      { id: 'six-kind', example: ex([6, 6, 6, 6, 6, 6]) },
      { id: 'chance', example: ex([1, 3, 5], true) },
    ];
    const comboRows = COMBO_ORDER.map(({ id, example }) => `
      <div class="combo-row${id === 'six-kind' ? ' hero' : ''}">
        <span class="combo-ex">${example}</span>
        <span class="combo-nm">${t(`combo.${id}` as I18nKey)}</span>
        <span class="combo-val">${id === 'chance' ? t('guide.chance-base') : COMBO_BASE[id]}</span>
      </div>`).join('');

    const FAMILIES: TalismanFamily[] = ['fissazioni', 'pirotecnici', 'spettrali', 'alchemici', 'rischio'];
    const talSections = FAMILIES.map((fam) => {
      const rows = TALISMANS.filter((d) => d.family === fam).map((d) => `
        <div class="guide-tal">
          <span class="guide-tal-name fam-${fam}">${t(`tal.${d.id}.name` as I18nKey)}</span>
          <span class="guide-tal-rar">${t(`rarity.${d.rarity}` as I18nKey)} · ◉ ${d.price}</span>
          <span class="guide-tal-desc">${t(`tal.${d.id}.desc` as I18nKey)}</span>
        </div>`).join('');
      return `<h4 class="guide-fam fam-${fam}">${t(`fam.${fam}` as I18nKey)}</h4>${rows}`;
    }).join('');

    const diceRows = (Object.keys(DIE_PRICE) as (keyof typeof DIE_PRICE)[]).map((kind) => `
      <div class="guide-tal">
        <span class="guide-tal-name">${t(`die.${kind}.name` as I18nKey)}</span>
        <span class="guide-tal-rar">◉ ${DIE_PRICE[kind]}</span>
        <span class="guide-tal-desc">${t(`die.${kind}.desc` as I18nKey)}</span>
      </div>`).join('');

    const section = (h: I18nKey, p: I18nKey, extra = '') => `
      <section class="guide-sec">
        <h3>${t(h)}</h3>
        <p>${t(p)}</p>
        ${extra}
      </section>`;

    const charRows = (Object.keys(CHARACTERS) as CharacterId[]).map((id) => `
      <div class="guide-tal">
        <span class="guide-tal-name">${t(`char.${id}.name` as I18nKey)}</span>
        <span class="guide-tal-desc">${t(`char.${id}.desc` as I18nKey)}</span>
      </div>`).join('');

    const bossRows = (['no-sei', 'pool-rilanci', 'minimo-tris', 'tremante'] as const).map((b) => `
      <div class="guide-tal">
        <span class="guide-tal-name">☠ ${t(`boss.${b}.name` as I18nKey)}</span>
        <span class="guide-tal-desc">${t(`boss.${b}.desc` as I18nKey)}</span>
      </div>`).join('');

    this.els.guide.classList.remove('hidden');
    this.els.guide.innerHTML = `
      <div class="sheet guide">
        <h2 class="sheet-title">${t('guide.title')}</h2>
        ${section('guide.goal.h', 'guide.goal.p')}
        ${section('guide.hand.h', 'guide.hand.p')}
        ${section('guide.score.h', 'guide.score.p')}
        ${section('guide.combos.h', 'guide.combos.p', `<div class="combo-table">${comboRows}</div>`)}
        ${section('guide.tilt.h', 'guide.tilt.p')}
        ${section('guide.boss.h', 'guide.boss.p', bossRows)}
        ${section('guide.shop.h', 'guide.shop.p')}
        ${section('guide.char.h', 'guide.char.p', charRows)}
        ${section('guide.tal.h', 'guide.tal.p', talSections)}
        ${section('guide.dice.h', 'guide.dice.p', diceRows)}
        <button type="button" class="btn primary big" data-el="guide-close">${t('guide.close')}</button>
      </div>
    `;
    const close = (): void => {
      this.els.guide.classList.add('hidden');
      this.els.guide.innerHTML = '';
    };
    this.els.guide.querySelector('[data-el="guide-close"]')!.addEventListener('click', close);
    this.els.guide.addEventListener('click', (e) => {
      if (e.target === this.els.guide) close();
    });
  }

  // ── Fine run ─────────────────────────────────────────────────────────────

  private renderEnd(): void {
    const run = this.run;
    if (!run) return;
    const overlay = this.els.overlay;
    overlay.classList.remove('hidden');
    const won = this.phase === 'won';
    overlay.innerHTML = `
      <div class="sheet end${won ? ' win' : ''}">
        <h2 class="sheet-title big">${won ? t('won.title') : t('over.title')}</h2>
        <p class="end-round">${won ? t('won.sub') : t('over.reached', { n: run.round })}</p>
        <div class="end-stats">
          <div><b>${run.bestHand}</b><span>${t('over.best')}</span></div>
          <div><b>${run.handsPlayed}</b><span>${t('over.hands')}</span></div>
          <div><b>${run.talismans.length}</b><span>${t('over.talismans')}</span></div>
        </div>
        <button type="button" class="btn primary big" data-el="again">${t('over.again')}</button>
      </div>
    `;
    overlay.querySelector('[data-el="again"]')!.addEventListener('click', () => this.openCharacterSelect());
  }

  // ── Azioni ───────────────────────────────────────────────────────────────

  private renderActions(): void {
    const box = this.els.actions;
    box.innerHTML = '';
    const button = (label: string, cls: string, onTap: () => void, disabled = false): void => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `btn ${cls}`;
      b.textContent = label;
      b.disabled = disabled;
      b.addEventListener('click', onTap);
      box.appendChild(b);
    };

    if (this.phase === 'menu') {
      button(`❔ ${t('guide.open')}`, 'secondary', () => this.openGuide());
      button(t('menu.start'), 'primary big', () => this.openCharacterSelect());
      return;
    }
    if (this.phase === 'rolling' || this.phase === 'shop' || this.phase === 'over' || this.phase === 'won') {
      return;
    }
    const run = this.run;
    if (!run) return;

    if (this.phase === 'scored') {
      if (run.phase === 'hand') button(t('action.next'), 'primary big', () => this.startHandUI());
      return;
    }

    // choice
    if (canRerollFree(run)) {
      const left = Math.max(0, MAX_ROLLS - (run.hand?.rollsUsed ?? MAX_ROLLS));
      button(t('action.reroll', { n: left }), 'secondary', () => this.doReroll(false));
    } else {
      button(t('action.force'), 'danger', () => this.doReroll(true), !canRerollForced(run));
    }
    button(t('action.bank'), 'primary', () => this.bank());
    this.els.note.textContent = canRerollFree(run) ? t('hint.hold') : t('tilt.hint');
  }
}
